import {
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    Clock3,
    Lock,
    PartyPopper,
    RefreshCw,
    ShieldCheck,
    Sparkles,
    Users,
    Wallet,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './ExpensesLanding.module.css'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'

const STEP_BLUEPRINT = [
    {
        stepKey: 'step1_categories',
        stepNumber: 1,
        label: 'Choose your expense categories',
        description: 'Pick from standard categories or create your own to power reports.',
        timeEstimate: '30 seconds',
        isMandatory: true,
        isComingSoon: false,
    },
    {
        stepKey: 'step2_first_expense',
        stepNumber: 2,
        label: 'Log your first expense',
        description: 'Add any bill and bring your dashboard to life instantly.',
        timeEstimate: '2 minutes',
        isMandatory: true,
        isComingSoon: false,
    },
    {
        stepKey: 'step3_vendors',
        stepNumber: 3,
        label: 'Add your vendors',
        description: 'Save vendor details for faster entries every time.',
        timeEstimate: '3 minutes',
        isMandatory: false,
        isComingSoon: true,
    },
    {
        stepKey: 'step4_approval',
        stepNumber: 4,
        label: 'Configure approval workflow',
        description: 'Control who can approve expenses across your team.',
        timeEstimate: '2 minutes',
        isMandatory: false,
        isComingSoon: false,
    },
    {
        stepKey: 'step5_recurring',
        stepNumber: 5,
        label: 'Set up recurring expenses',
        description: 'Automate rent, subscriptions and salaries.',
        timeEstimate: '3 minutes',
        isMandatory: false,
        isComingSoon: true,
    },
    {
        stepKey: 'step6_team',
        stepNumber: 6,
        label: 'Invite your team',
        description: 'Grant login access to managers and accountants.',
        timeEstimate: '2 minutes',
        isMandatory: false,
        isComingSoon: false,
    },
]

const STEP_ROUTE_MAP = {
    step1_categories: '/settings/expenses',
    step2_first_expense: '/expenses/add',
    step4_approval: '/manage/employees',
    step6_team: '/manage/employees',
}

const parseActiveStore = (activeStore) => {
    try {
        return (activeStore && activeStore.trim() !== '') ? JSON.parse(activeStore) : null
    } catch {
        return null
    }
}

const mergeStepData = (apiSteps) => {
    const incoming = new Map((Array.isArray(apiSteps) ? apiSteps : []).map((step) => [step?.stepKey, step]))
    return STEP_BLUEPRINT.map((baseStep) => {
        const serverStep = incoming.get(baseStep.stepKey) || {}
        return {
            ...baseStep,
            ...serverStep,
            isMandatory: typeof serverStep?.isMandatory === 'boolean' ? serverStep.isMandatory : baseStep.isMandatory,
            isComingSoon: typeof serverStep?.isComingSoon === 'boolean' ? serverStep.isComingSoon : baseStep.isComingSoon,
            isComplete: Boolean(serverStep?.isComplete),
            isActive: Boolean(serverStep?.isActive),
            meta: serverStep?.meta || {},
        }
    })
}

const getStepMetaText = (step) => {
    const meta = step?.meta || {}
    if (step.stepKey === 'step1_categories') return `${Number(meta.categoriesAdopted || 0)} categories active`
    if (step.stepKey === 'step2_first_expense') return `${Number(meta.totalExpensesLogged || 0)} expenses logged`
    if (step.stepKey === 'step4_approval') return `${Number(meta.teamMembersWithAccess || 0)} team members with access`
    if (step.stepKey === 'step6_team') return `${Number(meta.workersWithLoginAccess || 0)} of ${Number(meta.totalWorkers || 0)} workers with login`
    return ''
}

const getProgressDefaults = () => ({
    totalSteps: 4,
    totalComplete: 0,
    progressPercent: 0,
    mandatoryTotal: 2,
    mandatoryComplete: 0,
    recommendedTotal: 2,
    recommendedComplete: 0,
    isDashboardUnlocked: false,
    isSetupComplete: false,
    currentStep: 'step1_categories',
})

const ExpensesLanding = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)
    const storeIdentifier = activeStore?.storeIdentifier || ''

    const [isLoading, setIsLoading] = useState(true)
    const [errorMessage, setErrorMessage] = useState('')
    const [apiMessage, setApiMessage] = useState('')
    const [steps, setSteps] = useState(mergeStepData([]))
    const [progress, setProgress] = useState(getProgressDefaults())
    const [isCelebrationOpen, setIsCelebrationOpen] = useState(false)
    const getCardsPerView = useCallback(() => {
        if (typeof window === 'undefined') return 2
        return window.innerWidth <= 980 ? 1 : 2
    }, [])
    const [cardsPerView, setCardsPerView] = useState(getCardsPerView)
    const [sectionPage, setSectionPage] = useState({
        mandatory: 0,
        recommended: 0,
        comingSoon: 0,
    })

    const previousUnlockedRef = useRef(null)
    const hasShownUnlockCelebrationRef = useRef(false)

    const routeToDashboard = useCallback((showSetupStrip, payloadSteps, payloadProgress) => {
        navigate('/expenses/summary', {
            replace: true,
            state: {
                showSetupStrip,
                onboardingSteps: payloadSteps,
                onboardingProgress: payloadProgress,
            },
        })
    }, [navigate])

    const fetchOnboardingProgress = useCallback(async ({ silent = false } = {}) => {
        if (!storeIdentifier) {
            setErrorMessage('Please select a store first.')
            setIsLoading(false)
            return
        }

        if (!silent) setIsLoading(true)
        setErrorMessage('')

        try {
            const payload = { storeIdentifier }
            if (authCtx?.token) payload.token = authCtx.token

            const response = await fetch(BASE_URL_API + 'getExpensesOnboardingProgress', {
                method: 'POST',
                body: JSON.stringify(payload),
            })

            const result = await response.json()

            if (result?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            if (result?.status !== 'success') {
                setErrorMessage(result?.message || 'Unable to load onboarding progress.')
                return
            }

            const responseData = result?.response || {}
            const resolvedSteps = mergeStepData(responseData?.steps)
            const resolvedProgress = { ...getProgressDefaults(), ...(responseData?.progress || {}) }

            setApiMessage(result?.message || '')
            setSteps(resolvedSteps)
            setProgress(resolvedProgress)

            const isUnlocked = Boolean(resolvedProgress?.isDashboardUnlocked)
            const isSetupComplete = Boolean(resolvedProgress?.isSetupComplete)
            const shouldCelebrateUnlock = previousUnlockedRef.current === false && !hasShownUnlockCelebrationRef.current

            if (isUnlocked && isSetupComplete) {
                previousUnlockedRef.current = true
                routeToDashboard(false, resolvedSteps, resolvedProgress)
                return
            }

            if (isUnlocked && !isSetupComplete) {
                previousUnlockedRef.current = true
                if (shouldCelebrateUnlock) {
                    setIsCelebrationOpen(true)
                    return
                }
                routeToDashboard(true, resolvedSteps, resolvedProgress)
                return
            }

            previousUnlockedRef.current = false
        } catch {
            setErrorMessage('Unable to connect to server.')
        } finally {
            if (!silent) setIsLoading(false)
        }
    }, [authCtx, routeToDashboard, storeIdentifier])

    useEffect(() => {
        previousUnlockedRef.current = null
        hasShownUnlockCelebrationRef.current = false
        setIsCelebrationOpen(false)
        fetchOnboardingProgress({ silent: false })
    }, [fetchOnboardingProgress, storeIdentifier])

    useEffect(() => {
        if (progress?.isSetupComplete) return

        const onFocus = () => {
            fetchOnboardingProgress({ silent: true })
        }

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') onFocus()
        }

        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onVisibilityChange)

        return () => {
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }
    }, [fetchOnboardingProgress, progress?.isSetupComplete])

    useEffect(() => {
        const onResize = () => {
            setCardsPerView(getCardsPerView())
        }

        window.addEventListener('resize', onResize)
        return () => window.removeEventListener('resize', onResize)
    }, [getCardsPerView])

    const mandatorySteps = useMemo(() => steps.filter((step) => step.isMandatory), [steps])
    const recommendedSteps = useMemo(() => steps.filter((step) => !step.isMandatory && !step.isComingSoon), [steps])
    const comingSoonSteps = useMemo(() => steps.filter((step) => step.isComingSoon), [steps])

    useEffect(() => {
        setSectionPage((prev) => {
            const sanitize = (currentPage, totalItems) => {
                const totalPages = Math.max(1, Math.ceil(totalItems / cardsPerView))
                return Math.min(currentPage, totalPages - 1)
            }

            return {
                mandatory: sanitize(prev.mandatory, mandatorySteps.length),
                recommended: sanitize(prev.recommended, recommendedSteps.length),
                comingSoon: sanitize(prev.comingSoon, comingSoonSteps.length),
            }
        })
    }, [cardsPerView, mandatorySteps.length, recommendedSteps.length, comingSoonSteps.length])

    const progressPercent = Math.max(0, Math.min(100, Number(progress?.progressPercent || 0)))

    const onStepAction = (step) => {
        if (!step || step.isComingSoon) return
        const route = STEP_ROUTE_MAP[step.stepKey]
        if (!route) return

        const isMandatoryBlocked = step.isMandatory && !step.isComplete && !step.isActive
        if (isMandatoryBlocked) return

        navigate(route)
    }

    const onCelebrateContinue = () => {
        hasShownUnlockCelebrationRef.current = true
        setIsCelebrationOpen(false)
        routeToDashboard(true, steps, progress)
    }

    const getSectionPagination = (sectionKey, sectionSteps) => {
        const totalPages = Math.max(1, Math.ceil(sectionSteps.length / cardsPerView))
        const currentPage = Math.min(sectionPage?.[sectionKey] || 0, totalPages - 1)
        const start = currentPage * cardsPerView
        const visibleSteps = sectionSteps.slice(start, start + cardsPerView)

        return {
            totalPages,
            currentPage,
            visibleSteps,
        }
    }

    const goToPrevPage = (sectionKey, totalPages) => {
        setSectionPage((prev) => ({
            ...prev,
            [sectionKey]: Math.max(0, Math.min((prev?.[sectionKey] || 0) - 1, totalPages - 1)),
        }))
    }

    const goToNextPage = (sectionKey, totalPages) => {
        setSectionPage((prev) => ({
            ...prev,
            [sectionKey]: Math.max(0, Math.min((prev?.[sectionKey] || 0) + 1, totalPages - 1)),
        }))
    }

    const renderStepCard = (step) => {
        const metaText = getStepMetaText(step)
        const isMandatoryBlocked = step.isMandatory && !step.isComplete && !step.isActive
        const isDisabled = Boolean(step.isComingSoon || isMandatoryBlocked)

        const cardClass = [
            styles.stepCard,
            step.isComingSoon ? styles.stepCardComingSoon : '',
            step.isComplete ? styles.stepCardComplete : '',
            step.isActive ? styles.stepCardActive : '',
        ].join(' ').trim()

        const ctaLabel = step.isComingSoon
            ? 'Coming soon'
            : step.isComplete
                ? 'Revisit'
                : step.isActive
                    ? 'Start →'
                    : step.isMandatory
                        ? 'Complete previous step first'
                        : 'Setup →'

        return (
            <button
                key={step.stepKey}
                type="button"
                className={cardClass}
                onClick={() => onStepAction(step)}
                disabled={isDisabled}
            >
                <div className={styles.stepTopRow}>
                    <span className={styles.stepNumber}>Step {step.stepNumber}</span>
                    {step.isComingSoon && <span className={styles.soonBadge}><Lock size={12} /> Coming soon</span>}
                    {!step.isComingSoon && step.isComplete && <span className={styles.doneBadge}><CheckCircle2 size={12} /> Completed</span>}
                    {!step.isComingSoon && step.isActive && <span className={styles.activeBadge}><Sparkles size={12} /> Next action</span>}
                </div>

                <h3>{step.label}</h3>
                <p>{step.description}</p>

                <div className={styles.stepMetaRow}>
                    <span className={styles.eta}><Clock3 size={12} /> {step.timeEstimate || '—'}</span>
                    {metaText && <span className={styles.metaText}>{metaText}</span>}
                </div>

                <div className={styles.ctaRow}>
                    <span className={styles.ctaText}>{ctaLabel}</span>
                    {!step.isComingSoon && !isMandatoryBlocked && <ArrowRight size={14} />}
                </div>
            </button>
        )
    }

    const renderStepSection = ({ sectionKey, title, subtitle, sectionSteps }) => {
        const { totalPages, currentPage, visibleSteps } = getSectionPagination(sectionKey, sectionSteps)
        const hasPrev = currentPage > 0
        const hasNext = currentPage < totalPages - 1
        const dynamicCols = Math.max(1, Math.min(cardsPerView, visibleSteps.length || cardsPerView))

        return (
            <section className={styles.group}>
                <div className={styles.groupHead}>
                    <div className={styles.groupHeadText}>
                        <h2>{title}</h2>
                        <span>{subtitle}</span>
                    </div>
                    <div className={styles.groupNav}>
                        <button type="button" className={styles.groupNavBtn} disabled={!hasPrev} onClick={() => goToPrevPage(sectionKey, totalPages)}>
                            <ArrowLeft size={13} /> Prev
                        </button>
                        <span className={styles.groupNavCount}>{Math.min(currentPage + 1, totalPages)} / {totalPages}</span>
                        <button type="button" className={styles.groupNavBtn} disabled={!hasNext} onClick={() => goToNextPage(sectionKey, totalPages)}>
                            Next <ArrowRight size={13} />
                        </button>
                    </div>
                </div>

                <div className={styles.stepViewport} style={{ gridTemplateColumns: `repeat(${dynamicCols}, minmax(0, 1fr))` }}>
                    {visibleSteps.map(renderStepCard)}
                </div>
            </section>
        )
    }

    if (!storeIdentifier) {
        return (
            <div className={styles.page}>
                <div className={styles.stateCard}>
                    <h2>Select a store to continue</h2>
                    <p>Onboarding progress is store-specific. Pick an active store first.</p>
                </div>
            </div>
        )
    }

    if (isLoading) {
        return (
            <div className={styles.page}>
                <div className={styles.stateCard}>
                    <RefreshCw size={16} className={styles.spin} />
                    <h2>Preparing your expense setup</h2>
                    <p>Loading live onboarding progress from your store data…</p>
                </div>
            </div>
        )
    }

    if (errorMessage) {
        return (
            <div className={styles.page}>
                <div className={styles.stateCard}>
                    <h2>Could not load setup progress</h2>
                    <p>{errorMessage}</p>
                    <button type="button" className={styles.retryBtn} onClick={() => fetchOnboardingProgress({ silent: false })}>
                        Retry
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroTop}>
                    <span className={styles.heroBadge}><ShieldCheck size={14} /> Expense Setup Wizard</span>
                    <button type="button" className={styles.refreshBtn} onClick={() => fetchOnboardingProgress({ silent: false })}>
                        <RefreshCw size={14} /> Refresh
                    </button>
                </div>

                <h1>Set up your expense workspace</h1>
                <p>Super clean, super fast onboarding. Complete required steps to unlock your dashboard, then finish optional enhancements anytime.</p>

                <div className={styles.progressWrap}>
                    <div className={styles.progressInfo}>
                        <strong>{progress.totalComplete} of {progress.totalSteps} active steps complete</strong>
                        <span>{progress.progressPercent}%</span>
                    </div>
                    <div className={styles.progressTrack}>
                        <span className={styles.progressFill} style={{ width: `${progressPercent}%` }} />
                    </div>
                    <div className={styles.metricsRow}>
                        <span><Wallet size={13} /> Mandatory: {progress.mandatoryComplete}/{progress.mandatoryTotal}</span>
                        <span><Users size={13} /> Recommended: {progress.recommendedComplete}/{progress.recommendedTotal}</span>
                        {apiMessage && <span className={styles.apiMessage}>{apiMessage}</span>}
                    </div>
                </div>
            </section>

            {renderStepSection({
                sectionKey: 'mandatory',
                title: 'Required to continue',
                subtitle: 'Dashboard unlock depends on these',
                sectionSteps: mandatorySteps,
            })}

            {renderStepSection({
                sectionKey: 'recommended',
                title: 'Make it even better',
                subtitle: 'Optional but strongly recommended',
                sectionSteps: recommendedSteps,
            })}

            {renderStepSection({
                sectionKey: 'comingSoon',
                title: 'Coming soon',
                subtitle: 'Preview of upcoming productivity features',
                sectionSteps: comingSoonSteps,
            })}

            {isCelebrationOpen && (
                <div className={styles.modalBackdrop}>
                    <div className={styles.modalCard}>
                        <PartyPopper size={24} />
                        <h3>Your expense system is live!</h3>
                        <p>The dashboard is now unlocked. You can now log, track and analyze every rupee.</p>
                        <button type="button" onClick={onCelebrateContinue}>Go to Dashboard</button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default ExpensesLanding